import { Center, Text } from '@mantine/core';
import { formatOrder, type PayoutTraderOrder } from '~/entities/payout';
import { type TableData, type TableCell, type TableRow } from '~/shared/ui/Table';
import { CopyIcon } from '~/shared/ui/CopyIcon';
import { createOrderStatusBadge, createOrderCountdown, createOrderPriorityBadge } from '../../../../OrderManagement';

enum Values {
    AMOUNT = 'Сумма',
    CURRENCY = 'Валюта',
    RATE = 'Курс',
    PRIORITY = 'Приоритет',
    STATUS = 'Статус',
    EXPIRES_AT = 'До истечения',
    TRADER = 'Трейдер',
    COPY = 'Скопировать',
}

const prepareHeaders: TableCell[] = [
    {
        key: Values.AMOUNT,
        content: Values.AMOUNT,
        ta: 'center',
    },
    {
        key: Values.CURRENCY,
        content: Values.CURRENCY,
        ta: 'center',
    },
    {
        key: Values.RATE,
        content: Values.RATE,
        ta: 'center',
    },
    {
        key: Values.PRIORITY,
        content: Values.PRIORITY,
        ta: 'center',
    },
    {
        key: Values.STATUS,
        content: Values.STATUS,
        ta: 'center',
    },
    {
        key: Values.EXPIRES_AT,
        content: Values.EXPIRES_AT,
        ta: 'center',
    },
    {
        key: Values.TRADER,
        content: Values.TRADER,
        ta: 'center',
    },
    {
        key: Values.COPY,
        content: Values.COPY,
        ta: 'center',
    },
];

type PrepareRowsProps = {
    orders?: PayoutTraderOrder[];
};

const prepareRows = ({ orders }: PrepareRowsProps): TableRow<PayoutTraderOrder>[] => {
    if (!orders) {
        return [];
    }

    return orders.map((order) => {
        return {
            key: order.id?.value ?? 0,
            cells: [
                {
                    key: Values.AMOUNT,
                    content: (
                        <Text
                            ta='center'
                            size='sm'
                        >
                            {order.amount?.value}
                        </Text>
                    ),
                },
                {
                    key: Values.CURRENCY,
                    content: (
                        <Text
                            ta='center'
                            size='sm'
                        >
                            {formatOrder.currency(order?.currency)}
                        </Text>
                    ),
                },
                {
                    key: Values.RATE,
                    content: (
                        <Text
                            ta='center'
                            size='sm'
                        >
                            {order.rate?.value}
                        </Text>
                    ),
                },
                { key: Values.PRIORITY, content: createOrderPriorityBadge(order?.priority) },
                {
                    key: Values.STATUS,
                    content: createOrderStatusBadge(order.status, true),
                },
                {
                    key: Values.EXPIRES_AT,
                    content: (
                        <Text
                            component='div'
                            ta='center'
                        >
                            {createOrderCountdown(order?.expiresAt)}
                        </Text>
                    ),
                },
                {
                    key: Values.TRADER,
                    content: <Text ta='center'>{order.traderName?.value}</Text>,
                },
                {
                    key: Values.COPY,
                    content: (
                        <Center>
                            <CopyIcon
                                stopPropagation
                                value={`${order.id?.value}, ${order.amount?.value}${formatOrder.currency(order.currency)}, ${formatOrder.date(order.createdAt)}, ${order.requisiteV2?.card?.number || order.requisiteV2?.sbp?.phoneNumber}, ${order.status ? formatOrder.status(order.status) : 'Неизвестно'}`}
                            />
                        </Center>
                    ),
                },
            ],
            item: order,
        };
    });
};

type PrepareTableData = PrepareRowsProps;

export const createTableData = ({ orders }: PrepareTableData): TableData<PayoutTraderOrder> => {
    return {
        head: prepareHeaders,
        rows: prepareRows({ orders }),
        caption: orders?.length === 0 ? 'Ордеров нет' : undefined,
    };
};
